import { Button, Modal, StatusPill, formatCurrency } from '@hue/design-system';
import { Barcode, CheckCheck, Paperclip, PencilLine, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Entry } from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  methodLabels,
  payerNameOf,
  settledLabelOf,
  statusLabels,
  statusTones,
  termLabels,
} from './entry-labels.js';

/// A ficha do lançamento.
///
/// Existe porque a linha da lista mostra o resumo, e o resto do que foi digitado — observação,
/// onde reembolsar, aporte de origem — só aparecia abrindo a edição. Ver não pode exigir permissão
/// de alterar nem o risco de mudar algo sem querer. É também o que a linha inteira abre ao ser
/// clicada, para não haver ícone a procurar, e é a única forma de chegar às ações no celular.
export function EntryDetailsDialog({
  entry,
  timeZone,
  onClose,
  onSettle,
  onReceipts,
  onSlips,
  onEdit,
  onDelete,
}: {
  entry: Entry;
  timeZone: string;
  onClose: () => void;
  /// Ausente quando a pessoa não pode fazer aquilo — quem decide é a tela, que conhece as
  /// permissões; aqui só se mostra o que foi entregue.
  onSettle?: () => void;
  onReceipts?: () => void;
  onSlips?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const settled = entry.installments.filter((installment) => installment.settledOn !== null).length;
  const partial = entry.installments.filter(
    (installment) => installment.settledOn === null && installment.settledCents > 0,
  ).length;
  const day = (value: string) => formatDate(`${value}T12:00:00Z`, timeZone);

  return (
    <Modal
      open
      onOpenChange={onClose}
      title={entry.description}
      description={`${formatCurrency(entry.amountCents)} · comprado em ${day(entry.purchasedOn)}`}
    >
      <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs">
        <StatusPill tone={statusTones[entry.status]}>{statusLabels[entry.status]}</StatusPill>
        {entry.reimbursedCents > 0 ? (
          <span className="text-content-muted">
            {settledLabelOf(entry)}{' '}
            <strong className="tabular-nums text-content">
              {formatCurrency(entry.reimbursedCents)}
            </strong>
          </span>
        ) : null}
        {entry.outstandingCents > 0 ? (
          <span className="text-content-muted">
            Falta{' '}
            <strong className="tabular-nums text-content">
              {formatCurrency(entry.outstandingCents)}
            </strong>
          </span>
        ) : null}
      </div>

      <dl className="grid gap-px overflow-hidden rounded-xl border border-surface-border bg-surface-border">
        <Field label="Valor">
          <span className="tabular-nums">{formatCurrency(entry.amountCents)}</span>
          {entry.installmentCount ? (
            <span className="text-content-muted">
              {' '}
              · {entry.installmentCount}x de {formatCurrency(entry.installmentAmountCents ?? 0)}
            </span>
          ) : null}
        </Field>
        <Field label="Forma de pagamento">
          {methodLabels[entry.paymentMethod]} · {termLabels[entry.paymentTerm]}
        </Field>
        <Field label="Data da compra">{day(entry.purchasedOn)}</Field>
        <Field label={entry.installmentCount ? 'Vencimento da 1ª parcela' : 'Vencimento'}>
          {entry.dueOn ? day(entry.dueOn) : '—'}
        </Field>
        <Field label="Pago por">{payerNameOf(entry)}</Field>
        {entry.installments.length > 1 ? (
          <Field label="Parcelas baixadas">
            {settled} de {entry.installments.length}
            {partial > 0 ? <span className="text-warning"> · {partial} em parte</span> : null}
          </Field>
        ) : null}
        {entry.payeeName ? <Field label="Favorecido">{entry.payeeName}</Field> : null}
        {entry.reimburseTo ? <Field label="Reembolsar para">{entry.reimburseTo}</Field> : null}
        {entry.reimburseAt ? <Field label="Reembolsar onde">{entry.reimburseAt}</Field> : null}
        {entry.contribution ? (
          <Field label="Aporte de origem">{entry.contribution.description}</Field>
        ) : null}
        {entry.notes ? <Field label="Observação">{entry.notes}</Field> : null}
      </dl>

      {/* Aqui o botão ganha o rótulo por extenso: são poucos, há espaço, e é o lugar em que a
          pessoa vai procurar o que dá para fazer com esta compra. */}
      <div className="mt-6 flex flex-wrap gap-2 border-t border-surface-border pt-5">
        {onSettle ? (
          <Button variant="outline" size="sm" onClick={onSettle}>
            <CheckCheck className="size-4" />
            Parcelas
          </Button>
        ) : null}
        {onReceipts ? (
          <Button variant="outline" size="sm" onClick={onReceipts}>
            <Paperclip className="size-4" />
            Comprovantes
          </Button>
        ) : null}
        {onSlips ? (
          <Button variant="outline" size="sm" onClick={onSlips}>
            <Barcode className="size-4" />
            Boletos
          </Button>
        ) : null}
        {onEdit ? (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <PencilLine className="size-4" />
            Editar
          </Button>
        ) : null}
        {onDelete ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-danger hover:bg-danger/12 hover:text-danger sm:ml-auto"
            onClick={onDelete}
          >
            <Trash2 className="size-4" />
            Excluir
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 bg-surface-card px-4 py-3">
      <dt className="text-xs text-content-faint">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm text-content">{children}</dd>
    </div>
  );
}
